// React
import { memo, useCallback, useEffect, useState } from "react";

// Servisni sloj
import { getInvoiceExtractionSettingsAPI } from "services/api/settings/invoiceExtraction/api";
import {
  InvoiceExtractionPipeline,
  InvoiceExtractionSettings,
} from "services/api/settings/invoiceExtraction/types";

// UI
import { Badge } from "ui/components/Badge";
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";

import { getPipelineChoice } from "ui/modules/settings/pages/ApplicationSettingsPage/consts";
import ExtractionFlow from "./components/ExtractionFlow/ExtractionFlow";
import InvoiceExtractionEditCard from "./components/InvoiceExtractionEditCard/InvoiceExtractionEditCard";

/**
 * Pregled postavki čitanja ulaznih računa iz datoteke. Prikazuje se samo ono što odabrani način
 * stvarno koristi — model je uvijek jedan, a Document AI podaci samo kad on čita stranicu. Polja
 * stoje onemogućena kao prikaz, a izmjena ide kroz obrazac u modalu, isto kao ostale postavke.
 */
const InvoiceExtractionCard: React.FC = () => {
  const [settings, setSettings] = useState<InvoiceExtractionSettings | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setSettings(await getInvoiceExtractionSettingsAPI());
    } catch {
      // greška se prikazuje kroz snackbar
    }
  };

  const handleEditClick = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  const handleEditSuccess = useCallback((updated: InvoiceExtractionSettings) => {
    setSettings(updated);
    setShowEditModal(false);
  }, []);

  if (!settings) return null;

  const choice = getPipelineChoice(settings.pipeline);
  const isOcrPipeline = settings.pipeline === InvoiceExtractionPipeline.OCR;

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <p className="text-base font-bold text-slate-600 py-3">1. Čitanje računa</p>
          <p className="text-sm">
            Tko čita priložene račune (PDF, sken ili fotografija) i po kojim uputama iz njih vadi
            podatke.
          </p>
        </div>

        <div className="flex justify-between items-start flex-1 px-3 my-3">
          <div className="flex flex-col w-[500px] gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                text={settings.isConfigured ? "API ključ postavljen" : "Nedostaje API ključ"}
                variant={settings.isConfigured ? "success" : "danger"}
                height="small"
                showDot
              />

              {isOcrPipeline && (
                <Badge
                  text={
                    settings.isOcrAvailable ? "Document AI podešen" : "Document AI nije podešen"
                  }
                  variant={settings.isOcrAvailable ? "success" : "warning"}
                  height="small"
                  showDot
                />
              )}
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-slate-700">{choice.title}</p>
              <p className="text-xs text-slate-500">{choice.description}</p>
              <ExtractionFlow steps={choice.steps} />
            </div>

            <TextField
              type="text"
              name="model"
              label={choice.modelLabel}
              value={isOcrPipeline ? settings.ocrModel : settings.model}
              disabled
            />

            {isOcrPipeline && (
              <>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="googleProjectId"
                      label="Project ID"
                      value={settings.googleProjectId}
                      disabled
                    />
                  </div>

                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="googleProcessorId"
                      label="Processor ID"
                      value={settings.googleProcessorId}
                      disabled
                    />
                  </div>

                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="googleLocation"
                      label="Regija"
                      value={settings.googleLocation}
                      disabled
                    />
                  </div>
                </div>

                <TextField
                  type="text"
                  name="googleServiceAccount"
                  label="Ključ servisnog računa"
                  value={
                    settings.hasGoogleCredentials
                      ? settings.googleServiceAccount || "Postavljen"
                      : "Nije postavljen"
                  }
                  disabled
                />
              </>
            )}

            <TextArea
              name="prompt"
              label="Upute modelu"
              value={settings.prompt}
              rows={10}
              cssClassName="font-mono text-xs"
              disabled
            />
          </div>

          <div>
            <Button
              variant="outlined"
              children="Uredi"
              onClick={handleEditClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
      </div>

      <Modal show={showEditModal} onHide={handleEditClose}>
        <ModalHeader
          title="Postavke čitanja računa"
          icon="/media/images/add-icon.svg"
          onHide={handleEditClose}
        />
        <ModalBody>
          {showEditModal && (
            <InvoiceExtractionEditCard
              show={showEditModal}
              settings={settings}
              onSuccess={handleEditSuccess}
              onClose={handleEditClose}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default memo(InvoiceExtractionCard);
